import { TopicChannelThreadContinuationState } from "@redotech/cockroachdb-codegen/tables/shopper-ai/topic-channel-thread-continuation-state";
import * as z from "zod";

const temporalWorkflowSchema = z.object({
  kind: z.enum([
    "inbound",
    "generate",
    "queue",
    "send",
    "marketing_conversation",
    "marketing_coordinator",
  ]),
  label: z.string(),
  namespace: z.string(),
  workflowId: z.string(),
});

const marketingMessageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  channel: z.string(),
  direction: z.string(),
  content: z.string(),
  timestamp: z.date(),
  supportMessageId: z.string().nullable(),
  supportDraftId: z.string().nullable(),
});

const paritySide = {
  marketingMessage: marketingMessageSchema
    .pick({
      id: true,
      conversationId: true,
      direction: true,
      timestamp: true,
      content: true,
    })
    .nullable(),
  supportRecord: z
    .object({
      kind: z.enum(["message", "draft"]),
      id: z.string(),
      channelThreadId: z.string(),
      senderType: z.string(),
      timestamp: z.date(),
      content: z.string(),
    })
    .nullable(),
};

const messageMetadataSchema = z.object({
  rawId: z.string(),
  topicId: z.string(),
  outboundMessageKind: z.string(),
  primaryTriggerKey: z.string().nullable(),
  additionalTriggerKeys: z.array(z.string()),
  primaryCustomerEventId: z.string().nullable(),
  additionalCustomerEventIds: z.array(z.string()),
  llmThreadId: z.string().nullable(),
  marketingConversationId: z.string().nullable(),
  generationMode: z.string().nullable(),
  sentViaHumanQueue: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const topicSchema = z.object({
  id: z.string(),
  rawId: z.string(),
  kind: z.enum([
    "shopper_support",
    "shopper_agent_conversation",
    "shopper_agent_outreach",
  ]),
  continuationState: z.enum(TopicChannelThreadContinuationState),
  linkedAt: z.date(),
  detachedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
  supportTicket: z
    .object({
      status: z.string(),
      priority: z.string().nullable(),
      assigneeType: z.string().nullable(),
      assigneeId: z.string().nullable(),
      ticketNumber: z.string().nullable(),
      queueId: z.string().nullable(),
      closureReason: z.string().nullable(),
      snoozedUntil: z.date().nullable(),
      legacyConversationId: z.string().nullable(),
      legacyLlmThreadId: z.string().nullable(),
      deletedAt: z.date().nullable(),
    })
    .nullable(),
  shopperAgentConversation: z
    .object({
      channel: z.string(),
      customerId: z.string(),
      generatedDiscountCodeKeys: z.unknown().nullable(),
    })
    .nullable(),
  shopperAgentOutreach: z
    .object({
      primaryTriggerKey: z.string().nullable(),
      primaryCustomerEventId: z.string().nullable(),
      origin: z.string().nullable(),
      automationKind: z.string().nullable(),
      generationMode: z.string().nullable(),
      sentViaHumanQueue: z.boolean(),
      campaignId: z.string().nullable(),
      automationId: z.string().nullable(),
      automationStepId: z.string().nullable(),
    })
    .nullable(),
});

const llmThreadAssociationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("message_metadata"),
    messageId: z.string(),
    topicId: z.string(),
  }),
  z.object({
    kind: z.literal("draft_agent_run"),
    draftId: z.string(),
    agentRunId: z.string(),
    draftKind: z.string(),
    status: z.string(),
    deletedAt: z.date().nullable(),
  }),
  z.object({
    kind: z.literal("channel_thread_llm_state"),
    agentMode: z.string(),
  }),
]);

export const getShopperAgentObservability = {
  name: "getShopperAgentObservability" as const,
  input: z.object({ channelThreadId: z.string() }),
  output: z.object({
    observedAt: z.date(),
    team: z.object({ id: z.string(), name: z.string() }),
    channelThread: z.object({
      id: z.string(),
      rawId: z.string(),
      channel: z.string(),
      integrationId: z.string(),
      externalThreadId: z.string(),
      channelThreadKey: z.string(),
      deletedAt: z.date().nullable(),
      createdAt: z.date(),
    }),
    participants: z.array(
      z.object({
        participantType: z.string(),
        role: z.string(),
        customerId: z.string().nullable(),
        userId: z.string().nullable(),
        agentId: z.string().nullable(),
        systemActor: z.string().nullable(),
      }),
    ),
    customers: z.array(
      z.object({
        id: z.string(),
        displayName: z.string(),
        primary: z.boolean(),
      }),
    ),
    marketingConversations: z.array(
      z.object({
        id: z.string(),
        customerId: z.string(),
        channel: z.string(),
        type: z.string(),
        status: z.string(),
      }),
    ),
    topics: z.array(topicSchema),
    messages: z.array(
      z.object({
        id: z.string(),
        rawId: z.string(),
        content: z.string(),
        senderType: z.string(),
        origin: z.string(),
        channel: z.string(),
        integrationId: z.string(),
        processingStatus: z.string(),
        contentType: z.string(),
        externalMessageId: z.string(),
        senderCustomerId: z.string().nullable(),
        senderUserId: z.string().nullable(),
        sentAt: z.date(),
        createdAt: z.date(),
        updatedAt: z.date(),
        topicLinks: z.array(
          z.object({
            topicId: z.string(),
            rawTopicId: z.string(),
            topicKind: z.string(),
            source: z.string(),
            linkedAt: z.date(),
          }),
        ),
        metadata: messageMetadataSchema.nullable(),
        humanQueueSend: z
          .object({
            reviewerEmail: z.string(),
            queueType: z.string(),
            sentContentWasModified: z.boolean(),
            originalContent: z.string().nullable(),
            sentAt: z.date(),
          })
          .nullable(),
        marketingMatches: z.array(marketingMessageSchema),
        temporalWorkflows: z.array(temporalWorkflowSchema),
      }),
    ),
    drafts: z.array(
      z.object({
        id: z.string(),
        rawId: z.string(),
        topicId: z.string().nullable(),
        content: z.string(),
        plainContent: z.string().nullable(),
        senderType: z.string(),
        status: z.string(),
        kind: z.string(),
        candidateState: z.string().nullable(),
        version: z.number(),
        agentRunId: z.string().nullable(),
        error: z.unknown().nullable(),
        providerMessageId: z.string().nullable(),
        deletedAt: z.date().nullable(),
        createdAt: z.date(),
        updatedAt: z.date(),
        marketingMatches: z.array(marketingMessageSchema),
        temporalWorkflows: z.array(temporalWorkflowSchema),
      }),
    ),
    channelThreadLlmStates: z.array(
      z.object({
        agentMode: z.string(),
        llmThreadId: z.string().nullable(),
        threadFound: z.boolean(),
        contextFingerprint: z.string().nullable(),
        createdAt: z.date(),
        updatedAt: z.date(),
      }),
    ),
    parity: z.object({
      matchedCount: z.number(),
      differentCount: z.number(),
      missingMarketingCount: z.number(),
      missingSupportCount: z.number(),
      differences: z.array(
        z.object({
          kind: z.enum([
            "content_mismatch",
            "missing_marketing",
            "missing_support",
          ]),
          ...paritySide,
        }),
      ),
    }),
    llmThreads: z.array(
      z.object({
        id: z.string(),
        found: z.boolean(),
        agentName: z.string().nullable(),
        parentThreadId: z.string().nullable(),
        createdAt: z.date().nullable(),
        updatedAt: z.date().nullable(),
        expiresAt: z.date().nullable(),
        metadata: z.array(z.object({ key: z.string(), value: z.string() })),
        associations: z.array(llmThreadAssociationSchema),
      }),
    ),
  }),
};

export type GetShopperAgentObservabilityOutput = z.infer<
  typeof getShopperAgentObservability.output
>;
